import { useState, useEffect, useRef, useCallback } from 'react';
import {
  X, Activity, Play, Trash2, ChevronDown, ChevronUp,
  Search, Filter, RefreshCw, Clock, CheckCircle,
  AlertCircle, Info, AlertTriangle, Wifi, WifiOff,
} from 'lucide-react';
import { autoTaskAPI } from '../api';
import './AutoTaskLogs.css';

const LEVEL_CONFIG = {
  info:    { label: 'INFO',    icon: Info,          cls: 'level-info' },
  success: { label: 'OK',      icon: CheckCircle,   cls: 'level-success' },
  warn:    { label: 'WARN',    icon: AlertTriangle,  cls: 'level-warn' },
  error:   { label: 'ERROR',   icon: AlertCircle,   cls: 'level-error' },
};

const EVENT_LABELS = {
  task_start:           'Запуск',
  task_end:             'Завершение',
  task_skip:            'Пропуск',
  task_error:           'Ошибка задачи',
  task_init:            'Инициализация',
  task_stopped:         'Остановлен',
  task_cron_trigger:    'Cron запуск',
  task_cron_error:      'Cron ошибка',
  task_info:            'Инфо',
  task_no_vps:          'Нет VPS',
  probe_offline:        'Probe офлайн',
  group_start:          'Группа',
  vps_check_start:      'Проверка VPS',
  vps_ok:               'VPS OK',
  vps_blocked:          'VPS заблокирован',
  vps_replaced:         'VPS заменён',
  vps_error:            'Ошибка VPS',
};

function formatTime(iso) {
  const d = new Date(iso);
  return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
}

function LogEntry({ entry }) {
  const [expanded, setExpanded] = useState(false);
  const cfg = LEVEL_CONFIG[entry.level] || LEVEL_CONFIG.info;
  const Icon = cfg.icon;
  const hasDetails = entry.details && Object.keys(entry.details).length > 0;

  return (
    <div className={`log-entry ${cfg.cls}`}>
      <div className="log-entry-main" onClick={() => hasDetails && setExpanded(v => !v)}>
        <div className="log-entry-left">
          <Icon size={13} className="log-icon" />
          <span className="log-time">{formatTime(entry.timestamp)}</span>
          <span className={`log-badge ${cfg.cls}`}>{cfg.label}</span>
          <span className="log-event">{EVENT_LABELS[entry.event] || entry.event}</span>
        </div>
        <div className="log-entry-right">
          <span className="log-message">{entry.message}</span>
          {hasDetails && (
            <button className="log-expand-btn" title="Подробности">
              {expanded ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
            </button>
          )}
        </div>
      </div>
      {expanded && hasDetails && (
        <div className="log-details">
          {Object.entries(entry.details).map(([k, v]) => (
            <div key={k} className="log-detail-row">
              <span className="log-detail-key">{k}:</span>
              <span className="log-detail-val">
                {typeof v === 'object' ? JSON.stringify(v) : String(v)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AutoTaskLogs({ onClose }) {
  const [logs, setLogs] = useState([]);
  const [status, setStatus] = useState(null);
  const [filterLevel, setFilterLevel] = useState('all');
  const [search, setSearch] = useState('');
  const [newestFirst, setNewestFirst] = useState(true);
  const [sseConnected, setSseConnected] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [isRunning, setIsRunning] = useState(false);

  const logsEndRef = useRef(null);
  const logsTopRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const esRef = useRef(null);

  // Load initial logs
  const loadLogs = useCallback(async () => {
    try {
      const [logsRes, statusRes] = await Promise.all([
        autoTaskAPI.getLogs(),
        autoTaskAPI.getStatus(),
      ]);
      setLogs(logsRes.data);
      setStatus(statusRes.data);
    } catch {}
  }, []);

  // SSE connection
  useEffect(() => {
    const token = localStorage.getItem('jwt_token');
    if (!token) return;

    const es = new EventSource(`/auto-task/logs/stream?token=${encodeURIComponent(token)}`);
    esRef.current = es;

    es.onopen = () => setSseConnected(true);
    es.onerror = () => setSseConnected(false);

    es.onmessage = (e) => {
      try {
        const entry = JSON.parse(e.data);
        setLogs(prev => [...prev, entry]);
        if (entry.event === 'task_start') setIsRunning(true);
        if (entry.event === 'task_end' || entry.event === 'task_error') setIsRunning(false);
      } catch {}
    };

    loadLogs();

    return () => { es.close(); esRef.current = null; };
  }, [loadLogs]);

  // Auto-scroll
  useEffect(() => {
    if (!autoScroll) return;
    const el = scrollContainerRef.current;
    if (!el) return;
    if (newestFirst) {
      el.scrollTop = 0;
    } else {
      el.scrollTop = el.scrollHeight;
    }
  }, [logs, newestFirst, autoScroll]);

  // Detect manual scroll to pause auto-scroll
  const handleScroll = () => {
    const el = scrollContainerRef.current;
    if (!el) return;
    if (newestFirst) {
      setAutoScroll(el.scrollTop < 80);
    } else {
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
      setAutoScroll(atBottom);
    }
  };

  const handleRunNow = async () => {
    try {
      await autoTaskAPI.run();
      setIsRunning(true);
    } catch {}
  };

  const handleClear = async () => {
    if (!confirm('Очистить все логи?')) return;
    try {
      await autoTaskAPI.clearLogs();
      setLogs([]);
    } catch {}
  };

  const filteredLogs = logs.filter(entry => {
    if (filterLevel !== 'all' && entry.level !== filterLevel) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        entry.message.toLowerCase().includes(q) ||
        entry.event.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const displayedLogs = newestFirst ? [...filteredLogs].reverse() : filteredLogs;

  const taskRunning = isRunning || status?.running;

  return (
    <div className="at-overlay" onClick={onClose}>
      <div className="at-panel" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="at-header">
          <div className="at-header-left">
            <Activity size={16} className="at-header-icon" />
            <span className="at-title">Auto Task Logs</span>
            <div className={`at-status-badge ${taskRunning ? 'running' : 'idle'}`}>
              {taskRunning ? (
                <><RefreshCw size={10} className="spin" /> Выполняется</>
              ) : (
                <><Clock size={10} /> Ожидание</>
              )}
            </div>
            <div className={`at-sse-badge ${sseConnected ? 'connected' : 'disconnected'}`}>
              {sseConnected
                ? <><Wifi size={10} /> Live</>
                : <><WifiOff size={10} /> Offline</>
              }
            </div>
          </div>

          <div className="at-header-right">
            <button
              className="at-btn at-btn-run"
              onClick={handleRunNow}
              disabled={taskRunning}
              title="Запустить сейчас"
            >
              <Play size={13} /> Запустить
            </button>
            <button className="at-btn at-btn-ghost" onClick={handleClear} title="Очистить логи">
              <Trash2 size={13} />
            </button>
            <button className="at-btn at-btn-ghost" onClick={onClose} title="Закрыть">
              <X size={15} />
            </button>
          </div>
        </div>

        {/* Status bar */}
        {status && (
          <div className="at-status-bar">
            <span>Cron: <b>{status.cronActive ? 'активен' : 'остановлен'}</b></span>
            {status.lastRunAt && (
              <span>
                Последний запуск: <b>{formatDate(status.lastRunAt)} {formatTime(status.lastRunAt)}</b>
                {status.lastRunDuration != null && <> ({status.lastRunDuration}с)</>}
              </span>
            )}
            <span>Логов: <b>{status.logCount}</b></span>
            <span>SSE клиентов: <b>{status.sseClients}</b></span>
          </div>
        )}

        {/* Toolbar */}
        <div className="at-toolbar">
          <div className="at-search-box">
            <Search size={12} className="at-search-icon" />
            <input
              className="at-search-input"
              type="text"
              placeholder="Поиск..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button className="at-search-clear" onClick={() => setSearch('')}>
                <X size={11} />
              </button>
            )}
          </div>

          <div className="at-filter-group">
            <Filter size={12} />
            {['all', 'info', 'success', 'warn', 'error'].map(lvl => (
              <button
                key={lvl}
                className={`at-filter-btn ${filterLevel === lvl ? 'active' : ''} ${lvl !== 'all' ? `filter-${lvl}` : ''}`}
                onClick={() => setFilterLevel(lvl)}
              >
                {lvl === 'all' ? 'Все' : LEVEL_CONFIG[lvl]?.label || lvl}
              </button>
            ))}
          </div>

          <button
            className={`at-order-btn ${newestFirst ? 'active' : ''}`}
            onClick={() => setNewestFirst(v => !v)}
            title={newestFirst ? 'Новые сверху' : 'Новые снизу'}
          >
            {newestFirst ? <ChevronDown size={13} /> : <ChevronUp size={13} />}
            {newestFirst ? 'Новые сверху' : 'Новые снизу'}
          </button>

          <span className="at-count">{filteredLogs.length} записей</span>
        </div>

        {/* Log list */}
        <div className="at-log-wrap">
          <div
            className="at-log-list"
            ref={scrollContainerRef}
            onScroll={handleScroll}
          >
            <div ref={newestFirst ? logsTopRef : undefined} />
            {displayedLogs.length === 0 ? (
              <div className="at-empty">
                <Activity size={28} />
                <p>Логов нет</p>
                <span>Нажмите «Запустить» или дождитесь автоматического запуска</span>
              </div>
            ) : (
              displayedLogs.map(entry => (
                <LogEntry key={entry.id} entry={entry} />
              ))
            )}
            <div ref={newestFirst ? undefined : logsEndRef} />
          </div>

          {/* Auto-scroll indicator */}
          {!autoScroll && (
            <button
              className="at-scroll-btn"
              onClick={() => {
                setAutoScroll(true);
                const el = scrollContainerRef.current;
                if (el) el.scrollTop = newestFirst ? 0 : el.scrollHeight;
              }}
            >
              {newestFirst ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
              В конец
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default AutoTaskLogs;
